import React from 'react';
import Field from '@data-driven-forms/react-form-renderer/dist/cjs/field';
import { isEqual } from 'lodash';

interface FormWizardTabsProps {
  currentStep: number;
  fields: Field[];
}

const tabIconClasses: Record<string, string> = {
  patient: 'fa-light fa-user-injured',
  'claim-information': 'fa-light fa-ballot-check',
  confirm: 'fa-light fa-receipt',
  success: 'fa-light fa-party-horn'
};

const normalize = (str: string) => str.toLowerCase().replace(/\s+/g, '-');

export const FormWizardTabs = React.memo(
  ({ currentStep, fields }: FormWizardTabsProps) => {
    const chevronOverlap = 28; // Fine-tuned to remove visual breaks

    return (
      <div className='wizard-tab-main'>
        {fields.map((field: Field, index: number) => {
          const isCurrent = index === currentStep;
          const isCompleted = index < currentStep;
          const isLast = index === fields.length - 1;
          const isFirst = index === 0;
          const isMiddle = !isFirst && !isLast;

          const label = field.title || field.name;
          const key = normalize(label);
          const iconClass = tabIconClasses[key] || 'fa fa-question-circle';

          let imageName = label.length > 7 ? 'chevron-tab-inactive.svg' : 'chevron-tab-inactive-short.svg';

          if (isCompleted) {
            if (isLast) imageName = 'chevron-tab-completed-rounded.svg';
            else if (isFirst) imageName = 'chevron-tab-completed-first.svg';
            else imageName = label.length > 8 ? 'chevron-tab-completed-middle.svg' : 'chevron-tab-completed-short.svg';
          } else if (isCurrent) {
            if (isFirst) imageName = 'chevron-tab-active-first.svg';
            else if (isLast) imageName = 'chevron-tab-active-rounded.svg';
            else imageName = label.length > 8 ? 'chevron-tab-active.svg' : 'chevron-tab-active-short.svg';
          } else {
            imageName = isLast
              ? 'chevron-tab-inactive-rounded.svg'
              : label.length > 8
              ? 'chevron-tab-inactive.svg'
              : 'chevron-tab-inactive-short.svg';
          }

          const textColor = isCompleted || isCurrent ? '#FFFFFF' : '#115099';

          const tabStyle: React.CSSProperties = {
            backgroundImage: `url(/images/${imageName})`,
            marginLeft: index > 0 ? -chevronOverlap : 0,
            zIndex: fields.length - index
          };

          return (
            <div key={field.name} className='wizard-tab' style={tabStyle}>
              <div
                className='wizard-tab-inner'
                style={{
                  paddingRight: isLast ? 8 : 12
                }}>
                <i
                  className={iconClass}
                  style={{
                    paddingLeft: '5px',
                    paddingRight: '8px',
                    color: textColor
                  }}
                />
                <span
                  className='wizard-tab-label'
                  style={{
                    color: textColor
                  }}>
                  {label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  },
  (prev, next) => isEqual(prev, next)
);

FormWizardTabs.displayName = 'FormWizardTabs';
